import { useTranslation } from "react-i18next"

import CommentIcon from "@/assets/icons/commentBasic/medium.svg?react"
import { toggleSupportChat } from "@/lib/support"
import { cn } from "@/lib/utils"

interface SupportChatButtonProps {
  /** Only when a header sets its icon colours explicitly; normally inherited. */
  iconClassName?: string
  className?: string
}

/**
 * The one way into support.
 *
 * Chatwoot's own floating bubble is hidden (`hideMessageBubble` in
 * ChatwootWidget), so this sits in the app header beside the LanguageSwitcher
 * and replaces the "Need help? Talk to us" text that used to be scattered
 * across the payment screens — those were styled `<span>`s with no handler and
 * no keyboard access, and one of them dismissed the buyer's payment error
 * instead of helping (MPE-1224).
 *
 * Deliberately stateless: Chatwoot owns open/closed and `toggle()` flips it, so
 * closing from inside the chat window cannot desynchronise this control.
 */
export default function SupportChatButton({ iconClassName, className }: SupportChatButtonProps) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      // Not disabled when Chatwoot is unavailable: the SDK loads asynchronously,
      // so a disabled button would be a race. `toggleSupportChat` is a safe
      // no-op until it arrives.
      onClick={() => toggleSupportChat()}
      aria-label={t("support.openChat", { defaultValue: "Chat with support" })}
      // Same box as its header siblings: HeaderNotificationBell's trigger is
      // `inline-flex items-center` and LanguageSwitcher's is an unstyled
      // trigger. Padding or a hover background here made this control a
      // different size from the two icons beside it.
      className={cn("inline-flex items-center", className)}
    >
      {/* No colour of its own — the bell and globe both inherit from the
          header, so forcing one here made this icon a different shade. */}
      <CommentIcon aria-hidden="true" className={cn("h-6 w-6", iconClassName)} />
    </button>
  )
}
